// Online orders for the Fashion layout. Orders arrive from Omni Connect as
// aggregator orders (s_order with an is_aggregator sale type); the store's
// side of fulfilment is kept on the server (cwr_fsh_fulfilment:
// getFashionOnlineOrders, accept/ready/handOver/ship/cancelFashionOnlineOrder).
// An order is accepted (ACC) for pickup or to ship, packed (RDY), billed with
// the aggregator bill, then handed over against the pickup code (DLV) or
// shipped (SHP). It can be cancelled (CAN) until it leaves the store.
// The channel is told each step through upsertOfdOrderStatus, the same call the
// Aggregator screen makes.
// A store bill shipped home (markFashionShipToHome) joins this list as channel
// "In store" with storeSale true and its address in shipTo. It has no online
// channel to tell, and can't be cancelled (it is returned from the bill).
import { isCancelledOfdStatus, normalizeOfdStatus, ofdStatusRank } from "../aggregatorOrderState";
import { parseShipTo } from "./shipTo";

export const STATUS_LABEL = { NEW: "New", ACC: "Accepted", RDY: "Packed", DLV: "Picked up", SHP: "Shipped", CAN: "Cancelled" };
export const MODE_LABEL = { PICKUP: "Pickup", SHIP: "Ship" };

// List filters: OPEN is NEW + ACC + RDY, DONE is DLV + SHP on the server.
export const FILTERS = [
  { value: "OPEN", label: "Open" },
  { value: "RDY", label: "Packed" },
  { value: "DONE", label: "Done" },
  { value: "CAN", label: "Cancelled" },
];

// The ofd status the channel should show once the store has reached a step.
export const CHANNEL_STATUS = { ACC: "AK", RDY: "FR", DLV: "DI", SHP: "DI", CAN: "CC" };

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;
const FIELDS =
  "orderId documentNo externalId channel ordered total customerName mobileNo ofdStatus invoiceStatus billed status mode courier awbNo otpTriesLeft readyAt closedAt shipTo storeSale lines { productId sku productName qty unitPrice }";
const REPLY = "{ status message recordId documentNo }";

export function buildOnlineOrdersQuery(bunitId, status = "OPEN", search = "") {
  const term = clean(search);
  return {
    query: `query { getFashionOnlineOrders(bunitId: ${str(bunitId)}, status: ${str(status || "OPEN")}${term ? `, search: ${str(term)}` : ""}) { ${FIELDS} }}`,
  };
}

export const buildAcceptOnlineOrderMutation = (orderId, bunitId, mode) => ({
  query: `mutation { acceptFashionOnlineOrder(orderId: ${str(orderId)}, bunitId: ${str(bunitId)}, mode: ${str(mode)}) ${REPLY} }`,
});
export const buildReadyOnlineOrderMutation = (orderId) => ({ query: `mutation { readyFashionOnlineOrder(orderId: ${str(orderId)}) ${REPLY} }` });
export const buildHandOverOnlineOrderMutation = (orderId, otp) => ({
  query: `mutation { handOverFashionOnlineOrder(orderId: ${str(orderId)}, otp: ${str(otp)}) ${REPLY} }`,
});
export const buildShipOnlineOrderMutation = (orderId, courier, awbNo) => ({
  query: `mutation { shipFashionOnlineOrder(orderId: ${str(orderId)}, courier: ${str(courier)}, awbNo: ${str(awbNo)}) ${REPLY} }`,
});
export const buildCancelOnlineOrderMutation = (orderId, reason) => ({
  query: `mutation { cancelFashionOnlineOrder(orderId: ${str(orderId)}, reason: ${str(reason)}) ${REPLY} }`,
});

// Server response → order rows, or null when the call failed.
export function readOnlineOrders(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionOnlineOrders;
  if (!Array.isArray(rows)) return null;
  return rows.map((r) => ({
    id: r.orderId,
    documentNo: r.documentNo || "",
    externalId: r.externalId || "",
    channel: r.channel || "",
    ordered: r.ordered || "",
    total: Number(r.total) || 0,
    customerName: r.customerName || "",
    mobileNo: r.mobileNo || "",
    ofdStatus: r.ofdStatus || "",
    billed: r.billed === true || r.billed === "Y",
    status: r.status || "NEW",
    mode: r.mode || "",
    courier: r.courier || "",
    awbNo: r.awbNo || "",
    otpTriesLeft: r.otpTriesLeft ?? null,
    closedAt: r.closedAt || "",
    storeSale: r.storeSale === true || r.storeSale === "Y",
    shipTo: parseShipTo(r.shipTo),
    lines: (Array.isArray(r.lines) ? r.lines : []).map((l) => ({ productId: l.productId, sku: l.sku || "", name: l.productName || l.sku || "", qty: Number(l.qty) || 0, unitPrice: l.unitPrice == null || l.unitPrice === "" || !Number.isFinite(Number(l.unitPrice)) ? null : Number(l.unitPrice) })),
  }));
}

// The channel cancelled an order the store is still working on: stop and put
// the stock back rather than pack or hand it over.
export const cancelledOnChannel = (order) => !order?.storeSale && order?.status !== "CAN" && isCancelledOfdStatus(order?.ofdStatus);

// Cancel is offered only for online orders not yet billed; the server refuses
// it for a store bill shipped home.
export const canCancelOnline = (order) => Boolean(order) && !order.storeSale;

// The channel is behind the store's step (a status update didn't get through):
// returns the ofd code to send again, or null when the channel is up to date.
export function channelBehind(order) {
  const want = CHANNEL_STATUS[order?.status];
  if (!want || order?.storeSale || order?.status === "NEW" || cancelledOnChannel(order)) return null;
  const have = normalizeOfdStatus(order?.ofdStatus);
  if (want === "CC") return have === "CC" ? null : want;
  return ofdStatusRank(have) >= ofdStatusRank(want) ? null : want;
}

// Reason codes the channel takes with a cancel, as on the Aggregator screen.
export const CANCEL_REASONS = [
  { value: "item_out_of_stock", label: "Out of stock" },
  { value: "option_out_of_stock", label: "Size or colour out of stock" },
  { value: "invalid_item", label: "Item not sold here" },
  { value: "store_busy", label: "Store busy" },
  { value: "store_closed", label: "Store closed" },
  { value: "total_missmatch", label: "Price or total wrong" },
  { value: "unspecified", label: "Other" },
];

// Channel updates that didn't get through, kept per store so "Update channel"
// still shows after a reload: { [orderId]: { code, reason, at } }. The server
// records the status locally even when the push fails, so the order's own
// ofdStatus can't tell. Entries older than a week are dropped.
export const PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const pendingKey = (bunitId) => `fashionChannelPending:${bunitId}`;

export function readChannelPending(bunitId, now = Date.now()) {
  if (!bunitId) return {};
  try {
    const raw = JSON.parse(localStorage.getItem(pendingKey(bunitId)) || "{}");
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v && v.code && now - (Number(v.at) || 0) < PENDING_MAX_AGE_MS));
  } catch {
    return {};
  }
}

export function writeChannelPending(bunitId, pending) {
  if (!bunitId) return;
  try {
    localStorage.setItem(pendingKey(bunitId), JSON.stringify(pending));
  } catch {
    // Storage full or blocked: the button still shows until the page reloads.
  }
}

// The code the customer shows at the counter. Ours are six digits; a channel's
// own code may carry letters, so only the shape is checked here.
export function validatePickupCode(code) {
  const c = String(code ?? "").replace(/\s+/g, "");
  if (!c) return { error: "Ask the customer for the pickup code from their order message." };
  if (!/^[A-Za-z0-9]{4,12}$/.test(c)) return { error: "The pickup code is 4 to 12 letters or digits." };
  return { code: c };
}

export function validateShipment(courier, awbNo) {
  const c = clean(courier);
  const a = clean(awbNo);
  if (!c) return { error: "Enter the courier." };
  if (!a) return { error: "Enter the AWB / tracking number." };
  if (!/^[A-Za-z0-9-]{4,40}$/.test(a)) return { error: "The tracking number is letters and digits only." };
  return { courier: c, awbNo: a };
}

// One price across channels: lines where the online store charged a different
// price per piece from the store's own price today (price list plus markdown).
// storePriceFor(productId) gives the store price, or null when the till doesn't
// know the product. Differences under PRICE_TOLERANCE (rounding) are ignored.
export const PRICE_TOLERANCE = 0.5;

export function priceGaps(order, storePriceFor) {
  if (typeof storePriceFor !== "function") return [];
  const gaps = [];
  (order?.lines || []).forEach((l) => {
    if (l.unitPrice == null) return;
    const store = storePriceFor(l.productId);
    if (store == null || !Number.isFinite(Number(store))) return;
    if (Math.abs(Number(store) - l.unitPrice) >= PRICE_TOLERANCE) gaps.push({ productId: l.productId, name: l.name, online: l.unitPrice, store: Number(store) });
  });
  return gaps;
}

export const itemsCount = (order) => (order?.lines || []).reduce((n, l) => n + l.qty, 0);

// Returns of online orders. The customer paid the online store, so the till
// doesn't pay cash back for them: they go to store credit (credit note) or an
// exchange. verifyReturn names the sale type but not whether it's an
// aggregator one, so the till's sale types settle it; an order opened from
// the Online orders panel is known to be online straight away.
export const ONLINE_RETURN_NOTE = "Online order: paid to the online store, so it goes back as store credit or an exchange, not cash.";
export const ONLINE_RETURN_BLOCKED = "This online order can't be refunded at the till, and store credit and exchange are switched off here. Ask the customer to return it through the online store.";

export function aggregatorSaleTypeIds(saleTypes) {
  const ids = new Set();
  (Array.isArray(saleTypes) ? saleTypes : []).forEach((st) => {
    const t = st?.cwrSaletype || st;
    if (t?.isAggregator === "Y" && t?.cwrSaletypeId) ids.add(t.cwrSaletypeId);
  });
  return ids;
}

const startedOnline = new Set();
export const markOnlineReturn = (sOrderID) => sOrderID && startedOnline.add(sOrderID);
export const clearOnlineReturns = () => startedOnline.clear();

export function isOnlineSale(order, aggregatorIds) {
  if (!order) return false;
  if (order.sOrderID && startedOnline.has(order.sOrderID)) return true;
  if (order.saleType?.isAggregator === "Y") return true;
  const id = order.saleType?.cwrSaletypeId;
  return Boolean(id && aggregatorIds?.has(id));
}

// Core's return options hide Refund for an online order (useReturnPolicyStore);
// the return goes ahead only when store credit or an exchange is on.
export const onlineReturnAllowed = (posConfig) => posConfig?.allowReturnCreditNote === "Y" || posConfig?.allowReturnExchange === "Y";
